import { Component, OnInit, ViewChild, Input, Renderer2, Inject, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { forkJoin, isObservable, Subscription, Subject } from 'rxjs';
import { JobPointeTransferService } from './job-pointe-transfer.service';
import { JobPointeService } from 'src/app/service/job-pointe/job-pointe.service';
import { DOCUMENT } from '@angular/common';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { MediaService } from 'src/app/service/media/media.service';
import { PermissionsService } from 'src/app/service/auth/permissions.service';
import { JobBoardAttribute } from './types/job-board-attribute';
import { DialogService } from 'src/app/service/dialog/dialog.service';
import type { EmployeeNote } from './types/employee-note';

type ColorOption = {
  name: string
  textColor: string
  value: string
}

@Component({
  selector: 'app-job-pointe-transfer',
  templateUrl: './job-pointe-transfer.component.html',
  styleUrls: ['./job-pointe-transfer.component.scss'],
})
export class JobPointeTransferComponent implements OnInit, AfterViewInit {
  displayedNotesCount = 5;
  showLoadMoreButton = false;
  rowSubscription: Subscription;
  modalSubscription: Subscription;
  selectedRow: any = {};
  employeeDetailForm: FormGroup;
  modalIsOpen: boolean = false;
  isActiveWorkingStatus = false;
  jobBoardAttributes: Array<any> = [];
  empJobBoardAttributes: Array<any> = [];
  employeeHomeLocal: Array<{ id: number; name: string }> = [];
  employeeNoteLogs: Array<{ id: number; note: string; userId: number; createdAt: string; updatedAt: string; isArchived: boolean }> = [];
  formData: FormData = new FormData();
  shirtSizeOptions: Array<string> = [
    'X-Small',
    'Small',
    'Medium',
    'Large',
    'LT',
    'XL',
    'XLT',
    'XXL',
    '2XLT',
    '3XL',
    '3XLT',
    '4XL',
  ];
  colorPalette: ColorOption[] = [
    { value: '#FFFFFF', name: 'White', textColor: '#212529' },
    { value: '#CBDDC3', name: 'Green', textColor: '#212529' },
    { value: '#FFE070', name: 'Yellow', textColor: '#212529' },
    { value: '#F5D3D6', name: 'Red', textColor: '#212529' },
    { value: '#9BC6D3', name: 'Blue', textColor: '#212529' },
  ];
  selectedNote: EmployeeNote | null = null

  constructor(
    private jobPointeTransferService: JobPointeTransferService,
    private router: Router,
    private formBuilder: FormBuilder,
    private jobPointeService: JobPointeService,
    private renderer2: Renderer2,
    private momentService: MomentService,
    private toastService: ToastService,
    private mediaService: MediaService,
    private permissionsService: PermissionsService,
    private dialogService: DialogService,
    @Inject(DOCUMENT) private _document,
  ) { this.initializeForm(); }

  @Input() refreshEmployeesSource: Subject<boolean>;
  @ViewChild('empProfileModal') empProfileModal: ElementRef;
  @ViewChild('shadeSelect') shadeSelect: ElementRef;
  @HostListener('document:click', ['$event'])
  clickedOut(event) {
    if (!this.empProfileModal?.nativeElement) return;
    if (event.target.className.includes('overlay')) this.handleClose();
  }

  ngOnInit(): void {
    this.jobPointeService.read['job_board_attributes']({ order: 'name,ASC' }).subscribe(res => {
      this.jobBoardAttributes = res.data.filter(attribute => attribute.status === true);
    });
    this.modalSubscription = this.jobPointeTransferService.modalIsOpen.subscribe(value => {
      this.modalIsOpen = value;
    });

    this.rowSubscription = this.jobPointeTransferService.selectedRow.subscribe(value => {
      if (Object.keys(value).length) {
        this.selectedRow = value;
        this.fetchEmployeeData(value.id);
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.jobPointeTransferService.close();
      }
    });
  }

  ngAfterViewInit() {
    // Subscribe to shade value changes
    this.employeeDetailForm.get('shade').valueChanges.subscribe(() => {
      this.updateSelectedColorDisplay();
    });
  }

  ngOnDestroy(): void {
    this.jobPointeTransferService.selectedRowSource.next({});
    if (this.rowSubscription) this.rowSubscription.unsubscribe();
    if (this.modalSubscription) this.modalSubscription.unsubscribe();
  }

  updateSelectedColorDisplay() {
    if (!this.shadeSelect?.nativeElement) return;

    const selectElement = this.shadeSelect.nativeElement;
    const selectedValue = this.employeeDetailForm.get('shade').value;

    if (selectedValue) {
      const selectedColor = this.colorPalette.find(c => c.value === selectedValue);

      if (selectedColor) {
        selectElement.style.backgroundColor = selectedColor.value;
        selectElement.style.color = selectedColor.textColor;
      } else {
        selectElement.style.backgroundColor = selectedValue;
      }
    } else {
      // Reset to default if no value
      selectElement.style.backgroundColor = '';
      selectElement.style.color = '';
    }
  }

  initializeForm() {
    this.employeeDetailForm = this.formBuilder.group({
      shade: [''],
    });
  }

  private fetchEmployeeData(employeeId: number) {
    this.jobPointeService.read['employee'](employeeId, {
      embed: 'employeeHomeLocal,employeeJobBoardAttributes,employeeNoteLogs,user.thumbnail',
      'scope[activeEmployees]': 'false',
    }).subscribe(({ data }) => {
      this.selectedRow = {
        ...this.selectedRow,
        ...data,
        employeeNoteLogs: data?.employeeNoteLogs,
        jobBoardAttributes: data?.jobBoardAttributes,
        employeeHomeLocal: data?.employeeHomeLocal,
      };

      this.empJobBoardAttributes = data?.jobBoardAttributes;
      this.employeeHomeLocal = data?.employeeHomeLocal;
      this.employeeNoteLogs = [...(data?.employeeNoteLogs || [])].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      this.updateLoadMoreButton();
      this.createEmployeeDetailForm(this.selectedRow);

      this.modalIsOpen = true;

      setTimeout(() => {
        this.updateSelectedColorDisplay()
      }, 10)
    }, (error) => {
      this.toastService.showToast('Failed to load employee data', 'error');
      this.handleClose();
    }
    );
  }

  get displayedNotes() {
    const notes = this.employeeNoteLogs
      .filter(note => !note.isArchived)
      .slice(0, this.displayedNotesCount);
    return notes;
  }

  updateLoadMoreButton() {
    const visibleNotes = this.employeeNoteLogs.filter(note => !note.isArchived);
    this.showLoadMoreButton = visibleNotes.length > this.displayedNotesCount;
  }

  loadMoreNotes() {
    this.displayedNotesCount += 5;
    this.updateLoadMoreButton();
  }

  setDataToControl(field, value) {
    this.employeeDetailForm.get(field).setValue(value);
  }

  setJobBoardAttribute(attribute, field: string, value: string) {
    if (!value) return;
    const index = this.getAttributeIndex(attribute);
    this.jobBoardAttributesFormArray.at(index).patchValue({ [field]: value });
  }

  getAttributeIndex(attribute) {
    return this.jobBoardAttributesFormArray.value.indexOf(attribute.value);
  }

  handleUploadThumbnail(event: any, parentType: string) {
    if (!event?.target?.files) return

    const formData: FormData = new FormData();
    formData.append('parentId', this.selectedRow.id);
    formData.append('parentType', parentType);

    for (let i = 0; i < event.target.files.length; i++) {
      formData.append('files', event.target.files[i]);
    }

    formData.append('category', 'thumbnail');

    this.mediaService.create['employees'](formData).subscribe(
      res => {
        const data = { media: res.data[0] }
        const id = this.getPreferredId()

        this.selectedRow.thumbnail = data
        this.selectedRow.avatar = res.data[0]

        if (!id || id === this.selectedRow?.id) return

        this.mediaService.delete['employees'](id).subscribe()
      },
      error => {
        console.error(error);
      },
    );

  }

  getPreferredId() {
    if (this.selectedRow?.avatar?.id) return this.selectedRow.avatar.id
    if (this.selectedRow?.thumbnail?.length) return this.selectedRow.thumbnail[0]?.media?.id
    return this.selectedRow?.id
  }

  removeThumbnail() {
    const idToRemove = this.getPreferredId()

    this.mediaService.delete['employees'](idToRemove).subscribe(() => {
      delete this.selectedRow?.avatar?.url;
      this.selectedRow.thumbnail = []
    });
  }

  get jobBoardAttributesFormArray(): FormArray {
    return this.employeeDetailForm.get('job_board_attributes') as FormArray;
  }

  newJobAttribute(attr): FormGroup {
    return this.formBuilder.group(attr);
  }

  createEmployeeDetailForm(data) {
    if (this.employeeDetailForm) {
      this.employeeDetailForm = null;
    }

    // Create new form
    this.employeeDetailForm = this.formBuilder.group({
      user_id: data.id,
      is_on_job_board: data.isOnJobBoard,
      job_board_attributes: this.formBuilder.array(
        this.jobBoardAttributes
          .map(a => ({
            id: this.getMatchingEmployeeAttr(a) ? this.getMatchingEmployeeAttr(a).id ?? a.id : a.id,
            jobBoardAttributeId: a.id,
            name: a.name,
            userId: this.selectedRow.id,
            status: this.getMatchingEmployeeAttr(a) ? this.getMatchingEmployeeAttr(a).status : false,
            expirationDate: this.getMatchingEmployeeAttr(a)
              ? this.formatDate(this.getMatchingEmployeeAttr(a).expirationDate)
              : null,
          }))
          .map(a => this.newJobAttribute(a)),
      ),
      shade: [data?.shade || ''],
      status: data.status,
      shirt_size: data.shirtSize,
      termination_reason: data.terminationReason,
      holiday_pay_notes: data.holidayPayNotes,
      employeeNoteLogs: [''],
      vacation_days: data.vacationDays,
      holiday_pay_effective_date:
        data.holidayPayEffectiveDate !== undefined && data.holidayPayEffectiveDate !== ''
          ? this.formatDate(data.holidayPayEffectiveDate)
          : null,
      vacation_pay_effective_date:
        data.vacationPayEffectiveDate !== undefined && data.vacationPayEffectiveDate !== ''
          ? this.formatDate(data.vacationPayEffectiveDate)
          : null,
      vacation_pay_notes: data.vacationPayNotes,
      has_vacation_pay: data.hasVacationPay,
      has_holiday_pay: data.hasHolidayPay,
      driving_license: data.drivingLicense || '',
      emergency_contact: data.emergencyContact || '',
      emergency_contact_number: data.emergencyContactNumber || '',
    });
  }

  getMatchingEmployeeAttr(attr) {
    return this.empJobBoardAttributes?.find(empAttr => empAttr.jobBoardAttribute.id === attr.id);
  }

  formatDrivingLicense(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove non-alphanumeric characters
    value = value.replace(/[^A-Za-z0-9]/g, '');

    // Limit to 15 characters
    if (value.length > 15) {
      value = value.slice(0, 15);
    }

    input.value = value;

    this.employeeDetailForm.get('driving_license').setValue(value, { emitEvent: false });
  }

  formatEmergencyContact(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove numbers but allow letters, spaces, and special characters
    value = value.replace(/[0-9]/g, '');

    input.value = value;
    this.employeeDetailForm.get('emergency_contact').setValue(value, { emitEvent: false });
  }

  formatEmergencyContactNumber(event: Event) {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    const selectionStart = input.selectionStart || 0;

    // Remove all non-digits
    let numbers = raw.replace(/\D/g, '');

    // Apply format (XXX) XXX-XXXX
    let formatted = '';
    if (numbers.length <= 3) {
      formatted = numbers;
    } else if (numbers.length <= 6) {
      formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }

    // Set formatted value
    input.value = formatted;
    this.employeeDetailForm.get('emergency_contact_number')?.setValue(formatted, { emitEvent: false });

    // Restore cursor position
    setTimeout(() => {
      input.setSelectionRange(formatted.length, formatted.length);
    });
  }


  formatDate(date) {
    if (!date) return null;
    return this.momentService.formateDate(date, 'MM/DD/YYYY');
  }

  formatPhoneNumber(string) {
    if (!string) return '';
    let cleaned = ('' + string).replace(/\D/g, '');
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
  }

  timedModalClose = () =>
    new Promise(r => {
      this.modalIsOpen = false;
      setTimeout(r, 10);
    });

  handleClose() {
    this.employeeDetailForm = null;
    this.selectedRow = {};
    this.jobPointeTransferService.close();
  }

  handleClear() {
    this.jobPointeTransferService.clearSelectedItems();
  }

  setForkedAttributes(attributes: JobBoardAttribute[]) {
    const newAttributes = attributes.map(attr => {
      const { id, expirationDate, jobBoardAttributeId, name, status, userId } = attr;

      return {
        id,
        expirationDate: this.formatDate(expirationDate),
        jobBoardAttributeId,
        name,
        status,
        userId,
      };
    });

    this.jobBoardAttributesFormArray.setValue(newAttributes);
  }

  triggerArchiveModal(note: EmployeeNote) {
    this.selectedNote = note

    this.dialogService.open('delete-employee-note-dialog').then(res => {
      if (!res) return this.selectedNote = null

      this.archiveNote(note)
    })
  }

  archiveNote(note: EmployeeNote): void {
    const payload = { isArchived: 1 };

    this.jobPointeService.update.employeeNoteLogArchiveStatus(note.id, payload).subscribe({
      next: () => {
        console.log('Note archived successfully');
        this.toastService.showToast('Note archived successfully', 'success');
        this.fetchEmployeeData(this.selectedRow.id);
      },
      error: (err) => {
        console.error('Error archiving note', err);
        this.toastService.showToast('Failed to archive note', 'error');
      },
    });
  }

  handleSave(_: unknown) {
    try {
      const isValidated = this.employeeDetailForm.valid;

      if (!isValidated) {
        this.toastService.showToast('Employee detail form is not valid, check input and try again', 'error');
        return;
      }

      // Get the new note from the form
      const newNote = this.employeeDetailForm.get('employeeNoteLogs').value?.trim();

      const updatedData = {
        ...this.employeeDetailForm.value,
      };

      // Remove employeeNoteLogs from the Payload if Empty
      if (!updatedData.employeeNoteLogs?.trim()) {
        delete updatedData.employeeNoteLogs;
      }

      const requests = [
        this.jobPointeService.create['employee_job_board_attributes'](this.jobBoardAttributesFormArray.value),
        this.jobPointeService.update['employees'](updatedData),
      ];

      forkJoin(requests).subscribe(res => {
        // Update jobboard attribute ids after creation
        this.setForkedAttributes(res[0].attributes);

        // Add the new note to the employeeNoteLogs array
        if (newNote) {
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          const newNoteLog = {
            id: this.employeeNoteLogs.length + 1,
            note: newNote,
            userId: this.selectedRow.id,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: currentUser.id || null,
            createdByUser: {
              employeeAlias: currentUser.employeeAlias
            },
            isArchived: false,
          };

          // Add the new note to the array and sort by createdAt
          this.employeeNoteLogs = [newNoteLog, ...this.employeeNoteLogs].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );

          this.updateLoadMoreButton();
        }

        // Clear the employeeNoteLogs textarea
        this.employeeDetailForm.get('employeeNoteLogs').setValue('');

        // Show success message
        this.toastService.showToast('Successfully saved employee details', 'success');

        // Trigger refresh if needed
        if (isObservable(this.refreshEmployeesSource)) {
          this.refreshEmployeesSource.next(true);
        }
      });
    } catch (error) {
      console.error(error);
      this.toastService.showToast('An error occurred saving employee details', 'error');
    }
  }

  handleExportUserDetails() {
    this.jobPointeService.export.employee_details({ employeeId: this.selectedRow.id }).subscribe(
      (res: any) => {
        const header = res.headers.get('Content-Disposition');
        let filename = 'employee_details.csv';

        if (header) {
          filename = header.split('filename=')[1]
            .replace(/"/g, '')
            .replace(/^undefined|undefined$/g, '')
            .replace(/\s+/g, '_')
            .trim() || filename;
        }

        // Construct filename if not from header
        if (filename === 'employee_details.csv') {
          const employeeName = `${this.selectedRow.firstName}_${this.selectedRow.lastName}`.replace(/\s+/g, '_');
          filename = `${employeeName}_employee_details.csv`;
        }

        this.downloadFile(res.body, filename);
      },
      (error) => console.error("Export failed:", error)
    );
  }

  downloadFile(blob: any, fileName: string) {
    const a = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  loadDatePicker(className = null, altField = null) {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    if (className != null) {
      s.text = `loadDatePicker('` + className + `','` + altField + `');`;
    } else {
      s.text = `loadDatePicker()`;
    }
    this.renderer2.appendChild(this._document.body, s);
  }

  isAllowed(resource: string, action: string) {
    const resources = {
      job_board_attributes: 'job_board_attributes',
      job_pointe_employees: 'job_pointe_employees',
    };
    const resource1 = resources[resource];
    return this.permissionsService.allow({ resource1, action });
  }
}
